// @ts-check

const { NOTIFICATIONS_AVAILABLE_TYPES } = require('../constants/_notifications')
const { $defaulter } = require('./utils')
const { isUUID, isSimpleObject } = require('../validators')
const { DOCUMENT_TEMPLATES_TYPES } = require('../constants/_documentTemplates')

/**
* Логика настроек для нового сервиса уведомлений
*/

/**
* NOTIFICATIONS_SETTINGS_TEMPLATE - Список допустимых видов настроек уведомлений.
*   templates - дефолтные шаблоны для каждого типа юзера
*      me - шаблоны для моих уведомлений
*      manager - шаблоны для уведомлений о событиях моих дочерних юзеров.
*                Опционально, если не задано то менеджер или реселлер не сможет подписываться на данное уведомление
*   У каждого типа есть набор доступных каналов уведомлений, каждому из которых настраиваются параметрры шаблона:
*      subject - тема / заголовок письма, доступно в некоторых каналах
*      body - основной текст шаблона, есть в каждом канале
*      каждому каналу могут быть добавлены кастомные свойства, не забудь только поменять templateCleanersByType
*   settingsValidation - коллбек для валидации настроек. Возвращает либо null, либо объект с настройками
*/

/**
 * @typedef { { subject: string, body: string } } NotificationTemplateTextSubject
 * @typedef { { body: string } } NotificationTemplateText
 */
/**
 * @template T
 * @typedef { { enabledByDefault: boolean, template: T } } NotificationChannelSettings
 */
/**
 * @typedef { { 
 *  email?: NotificationChannelSettings<NotificationTemplateTextSubject>,
 *  sms?: NotificationChannelSettings<NotificationTemplateText>,
 *  web?: NotificationChannelSettings<NotificationTemplateTextSubject>,
 *  push?: NotificationChannelSettings<NotificationTemplateText>,
 *  tgBot?: NotificationChannelSettings<NotificationTemplateText>
 * } } NotificationSettings
 */

/**
 * @type { Record<string, {
 *  settings: {
 *    me?: NotificationSettings,
 *    manager?: NotificationSettings
 *  },
 *  settingsValidation: (...args: any[]) => any}>
 * }
 */
const NOTIFICATIONS_SETTINGS_TEMPLATE = {
  'tariff.update': {
    settings: {
      me: {
        email: {
          enabledByDefault: false,
          template: {
            subject: 'Ваш тариф изменился',
            body: 'Ваш тариф на сайте {{ domain }} изменился на {{ tariff.title }}.<br/>Проверьте свой новый тариф в личном кабинете {{ domain }}.'
          }
        },
        web: {
          enabledByDefault: false,
          template: {
            subject: 'Ваш тариф изменился',
            body: 'Ваш тариф изменился на {{ tariff.title }}.'
          }
        }
        // get [NOTIFICATIONS_AVAILABLE_TYPES.push]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // },
        // get [NOTIFICATIONS_AVAILABLE_TYPES.tgBot]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // }
      },
      manager: {
        email: {
          enabledByDefault: false,
          template: {
            subject: 'Тариф пользователя {{ user.username }} изменился',
            body: 'Тариф пользователя {{ user.username }} на сайте {{ domain }} изменился на {{ tariff.title }}.'
          }
        },
        web: {
          enabledByDefault: false,
          template: {
            subject: 'Тариф пользователя {{ user.username }} изменился',
            body: 'Тариф пользователя {{ user.username }} изменился на {{ tariff.title }}.'
          }
        }
        // get [NOTIFICATIONS_AVAILABLE_TYPES.push]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // },
        // get [NOTIFICATIONS_AVAILABLE_TYPES.tgBot]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // }
      }
    },
    settingsValidation: () => null
  },
  'balance.limit': {
    settings: {
      me: {
        email: {
          enabledByDefault: true,
          template: {
            subject: 'Критическое значение баланса в личном кабинете по адресу: {{ domain }}',
            body: 'Уважаемый Клиент,<br><br>Внимание!<br><br>Ваш баланс преодолел критическое значение.<br>Доступно к использованию: {{ balance }}<br>Напоминаем о необходимости его пополнить.<br>Логин: {{ user.username }} на <a href="{{ url }}">{{ domain }}</a><br><br>Если у вас возникли вопросы, свяжитесь со своим менеджером или напишите по адресу {{ manager.email }}.<br><br>С уважением {{ title }}'
          }
        },
        sms: {
          enabledByDefault: false,
          template: {
            body: 'Баланс на {{ domain }}: {{ balance }}. Пополните баланс.'
          }
        },
        web: {
          enabledByDefault: false,
          template: {
            subject: 'Достингнут порог баланса',
            body: 'Ваш баланс достинг порога, и теперь составляет {{ balance }}.'
          }
        }
      },
      manager: {
        email: {
          enabledByDefault: false,
          template: {
            subject: 'Критическое значение баланса на {{ domain }}',
            body: 'Баланс пользователя {{ user.username }} преодолел критическое значение<br>Баланс пользователя - {{ balance }}'
          }
        },
        sms: {
          enabledByDefault: false,
          template: {
            body: 'Баланс пользователя {{ user.username }}: {{ balance }}'
          }
        },
        web: {
          enabledByDefault: false,
          template: {
            subject: 'Достингнут порог баланса',
            body: 'Баланс пользователя {{ user.username }} достинг порога, и теперь составляет {{ balance }}.'
          }
        }
      }
    },
    settingsValidation: (limits = [
      {
        balance: 0,
        includeOverdraft: false,
        invoice: false
      },
      {
        balance: 0,
        includeOverdraft: true,
        invoice: false
      },
      {
        balance: 100,
        includeOverdraft: false,
        invoice: false
      },
      {
        balance: 100,
        includeOverdraft: true,
        invoice: false
      }
    ]) => {
      if (!Array.isArray(limits)) {
        return []
      }

      const result = []
      for (const limit of limits) {
        if (typeof limit !== 'object' || limit === null) {
          continue
        }

        const { balance, includeOverdraft, invoice } = limit
        if (typeof balance !== 'number') {
          continue
        }

        const filteredLimit = {
          balance,
          includeOverdraft: !!includeOverdraft,
          invoice: false
        }

        if (typeof invoice === 'object' && invoice !== null) {
          let SERVICE_PRICE = undefined
          if (invoice.data && invoice.data.SERVICE_PRICE) {
            SERVICE_PRICE = parseFloat(invoice.data.SERVICE_PRICE)
            if (isNaN(SERVICE_PRICE)) {
              SERVICE_PRICE = undefined
            }
          }

          if (
            isUUID(invoice.requisiteId) &&
            isUUID(invoice.companyId) &&
            invoice.type === DOCUMENT_TEMPLATES_TYPES.invoice &&
            SERVICE_PRICE
          ) {
            filteredLimit.invoice = {
              requisiteId: invoice.requisiteId,
              companyId: invoice.companyId,
              type: invoice.type,
              data: {
                SERVICE_PRICE: SERVICE_PRICE
              }
            }
          }
        }

        result.push(filteredLimit)
      }

      return result
    }
  },
  'balance.accrual': {
    settings: {
      me: {
        email: {
          enabledByDefault: true,
          template: {
            subject: 'Начисление баланса в личном кабинете по адресу: {{ domain }}',
            body: 'Уважаемый Клиент,<br><br>Ваш баланс пополнен на сумму {{ amount }}{{ currency }}.<br>Доступно к использованию: {{ balance }}{{ currency }}<br>Логин: {{ user.username }} на <a href="{{ url }}">{{ domain }}</a><br><br>Если у вас возникли вопросы, свяжитесь со своим менеджером или напишите по адресу {{ manager.email }}.<br><br>С уважением {{ title }}'
          }
        },
        web: {
          enabledByDefault: false,
          template: {
            subject: 'Начисление баланса',
            body: 'Ваш баланс пополнен на сумму {{ amount }}{{ currency }}.'
          }
        }
        // get [NOTIFICATIONS_AVAILABLE_TYPES.push]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // }
      },
      manager: {
        email: {
          enabledByDefault: false,
          template: {
            subject: 'Начисление баланса кабинета {{ user.username }}',
            body: 'Баланс пользователя {{ user.username }} пополнен на сумму {{ amount }}{{ currency }}.'
          }
        },
        web: {
          enabledByDefault: false,
          template: {
            subject: 'Начисление баланса',
            body: 'Баланс пользователя {{ user.username }} пополнен на сумму {{ amount }}{{ currency }}.'
          }
        }
        // get [NOTIFICATIONS_AVAILABLE_TYPES.push]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // }
      }
    },
    settingsValidation: () => null
  },
  'balance.daily': {
    settings: {
      me: {
        email: {
          enabledByDefault: false,
          template: {
            subject: 'Ежедневное уведомление о балансе в личном кабинете по адресу: {{ domain }}',
            body: 'Уважаемый Клиент,<br><br>Ваш баланс на сайте {{ domain }} составляет {{ balance }}{{ currency }}.<br>Логин: {{ user.username }} на <a href="{{ url }}">{{ domain }}</a><br><br>Если у вас возникли вопросы, свяжитесь со своим менеджером или напишите по адресу {{ manager.email }}.<br><br>С уважением {{ title }}'
          }
        }
        // get [NOTIFICATIONS_AVAILABLE_TYPES.tgBot]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // }
      },
      manager: {
        email: {
          enabledByDefault: false,
          template: {
            subject: 'Ежедневное уведомление о балансе пользователя {{ user.username }}',
            body: 'Баланс пользователя {{ user.username }} на сайте {{ domain }} составляет {{ balance }}{{ currency }}.'
          }
        }
        // get [NOTIFICATIONS_AVAILABLE_TYPES.tgBot]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // }
      }
    },
    settingsValidation: () => null
  },
  'auth.registration': {
    settings: {
      me: {
        email: {
          enabledByDefault: true,
          template: {
            subject: 'Спасибо за регистрацию на платформе СМС рассылок',
            body: 'Спасибо за регистрацию на платформе СМС рассылок.<br><br>Ваш логин и пароль для входа в платформу:<br>Логин: {{ username }}{{#if balance}}<br><br>Внимание!<br>Вам начислен тестовый баланс {{ balance }} рублей. Начинайте прямо сейчас!{{/if}}'
          }
        }
      },
      manager: {
        email: {
          enabledByDefault: false,
          template: {
            subject: 'Пользователь {{ username }} зарегистрирован',
            body: 'Пользователь {{ username }} зарегистрирован с балансом {{ balance }} рублей.'
          }
        }
      }
    },
    settingsValidation: () => null
  },
  'auth.recover': {
    settings: {
      me: {
        email: {
          enabledByDefault: true,
          template: {
            subject: 'Запрос смены пароля на {{ domain }}',
            body: 'Уважаемый Клиент,<br><br>Вы запросили смену пароля.<br>Для смены пароля перейдите по ссылке: <a href="{{ url }}/#/recover/${key}">{{ domain }}/#/recover/{{ key }}</a>.<br><br>Если вы не делали запрос на смену пароля, рекомендуем вам сменить пароль через личный кабинет.'
          }
        }
      },
      manager: {
        email: {
          enabledByDefault: false,
          template: {
            subject: 'Запрос смены пароля пользователя {{ username }}',
            body: 'Пользователь {{ username }} запросил смену пароля.'
          }
        }
      }
    },
    settingsValidation: () => null
  },
  'pattern.approved': {
    settings: {
      me: {
        email: {
          enabledByDefault: false,
          template: {
            subject: 'Ваш шаблон для модерации на сайте {{ domain }} одобрен!',
            body: 'Шаблон "{{ pattern.title }}" для {{ pattern.type }} одобрен!<br>Теперь вы можете отправлять сообщения с новым текстом!'
          }
        },
        web: {
          enabledByDefault: false,
          template: {
            subject: 'Ваш шаблон для модерации одобрен!',
            body: 'Шаблон "{{ pattern.title }}" для {{ pattern.type }} одобрен!'
          }
        }
        // get [NOTIFICATIONS_AVAILABLE_TYPES.push]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // },
        // get [NOTIFICATIONS_AVAILABLE_TYPES.tgBot]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // }
      },
      manager: {
        email: {
          enabledByDefault: false,
          template: {
            subject: 'Шаблон "{{ pattern.title }}" одобрен',
            body: 'Шаблон "{{ pattern.title }}" для {{ pattern.type }} одобрен!'
          }
        },
        web: {
          enabledByDefault: false,
          template: {
            subject: 'Шаблон "{{ pattern.title }}" одобрен',
            body: 'Шаблон "{{ pattern.title }}" для {{ pattern.type }} одобрен!'
          }
        }
        // get [NOTIFICATIONS_AVAILABLE_TYPES.push]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // },
        // get [NOTIFICATIONS_AVAILABLE_TYPES.tgBot]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // }
      }
    },
    settingsValidation: () => null
  },
  'pattern.rejected': {
    settings: {
      me: {
        email: {
          enabledByDefault: false,
          template: {
            subject: 'Ваш шаблон для модерации на сайте {{ domain }} отклонен!',
            body: 'Шаблон "{{ pattern.title }}" для {{ pattern.type }} отклонен!<br><br>Если у вас возникли вопросы, свяжитесь со своим менеджером или напишите по адресу {{ manager.email }}.'
          }
        },
        web: {
          enabledByDefault: false,
          template: {
            subject: 'Ваш шаблон для модерации отклонен!',
            body: 'Шаблон "{{ pattern.title }}" для {{ pattern.type }} отклонен!'
          }
        }
        // get [NOTIFICATIONS_AVAILABLE_TYPES.push]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // },
        // get [NOTIFICATIONS_AVAILABLE_TYPES.tgBot]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // }
      },
      manager: {
        email: {
          enabledByDefault: false,
          template: {
            subject: 'Шаблон "{{ pattern.title }}" отклонен',
            body: 'Шаблон "{{ pattern.title }}" для {{ pattern.type }} отклонен!'
          }
        },
        web: {
          enabledByDefault: false,
          template: {
            subject: 'Шаблон "{{ pattern.title }}" отклонен',
            body: 'Шаблон "{{ pattern.title }}" для {{ pattern.type }} отклонен!'
          }
        }
        // get [NOTIFICATIONS_AVAILABLE_TYPES.push]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // },
        // get [NOTIFICATIONS_AVAILABLE_TYPES.tgBot]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // }
      }
    },
    settingsValidation: () => null
  },
  'sendername.approved': {
    settings: {
      me: {
        email: {
          enabledByDefault: true,
          template: {
            subject: 'Ваше имя отправителя на сайте {{ domain }} одобрено!',
            body: 'Имя отправителя "{{ sendername.name }}" одобрено!<br>Теперь вы можете отправлять сообщения с новым именем отправителя!'
          }
        },
        web: {
          enabledByDefault: true,
          template: {
            subject: 'Ваше имя отправителя одобрено!',
            body: 'Имя отправителя "{{ sendername.name }}" одобрено!'
          }
        }
        // get [NOTIFICATIONS_AVAILABLE_TYPES.push]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // },
        // get [NOTIFICATIONS_AVAILABLE_TYPES.tgBot]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // }
      },
      manager: {
        email: {
          enabledByDefault: false,
          template: {
            subject: 'Имя отправителя "{{ sendername.name }}" одобрено',
            body: 'Имя отправителя "{{ sendername.name }}" одобрено!'
          }
        },
        web: {
          enabledByDefault: false,
          template: {
            subject: 'Имя отправителя "{{ sendername.name }}" одобрено',
            body: 'Имя отправителя "{{ sendername.name }}" одобрено!'
          }
        }
        // get [NOTIFICATIONS_AVAILABLE_TYPES.push]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // },
        // get [NOTIFICATIONS_AVAILABLE_TYPES.tgBot]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // }
      }
    },
    settingsValidation: () => null
  },
  'sendername.rejected': {
    settings: {
      me: {
        email: {
          enabledByDefault: true,
          template: {
            subject: 'Ваше имя отправителя на сайте {{ domain }} отклонено!',
            body: 'Имя отправителя "{{ sendername.name }}" отклонено!<br><br>Если у вас возникли вопросы, свяжитесь со своим менеджером или напишите по адресу {{ manager.email }}.'
          }
        },
        web: {
          enabledByDefault: true,
          template: {
            subject: 'Ваше имя отправителя отклонено!',
            body: 'Имя отправителя "{{ sendername.name }}" отклонено!'
          }
        }
        // get [NOTIFICATIONS_AVAILABLE_TYPES.push]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // },
        // get [NOTIFICATIONS_AVAILABLE_TYPES.tgBot]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // }
      },
      manager: {
        [NOTIFICATIONS_AVAILABLE_TYPES.email]: {
          subject: 'Title',
          body: 'No template text!'
        },
        get [NOTIFICATIONS_AVAILABLE_TYPES.web]() {
          return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        }
        // get [NOTIFICATIONS_AVAILABLE_TYPES.push]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // },
        // get [NOTIFICATIONS_AVAILABLE_TYPES.tgBot]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // }
      }
    },
    settingsValidation: () => null
  },
  'sendings.bulk.created': {
    settings: {
      me: {
        web: {
          enabledByDefault: false,
          template: {
            subject: 'Рассылка "{{ task.title }}" создана',
            body: 'Задача на рассылку создана.<br>Проверьте статус задачи в личном кабинете.'
          }
        }
      },
      manager: {
        web: {
          enabledByDefault: false,
          template: {
            subject: 'Пользователь {{ user.username }} создал рассылку',
            body: 'Массовая рассылка "{{ task.title }}" с ID {{ task.id }} создана!'
          }
        }
      }
    },
    settingsValidation: () => null
  },
  'sendings.bulk.paused': {
    settings: {
      me: {
        web: {
          enabledByDefault: false,
          template: {
            subject: 'Рассылка "{{ task.title }}" приостановлена',
            body: 'Задача на рассылку "{{ task.title }}" приостановлена.<br>Проверьте статус задачи в личном кабинете.'
          }
        }
      },
      manager: {
        web: {
          enabledByDefault: false,
          template: {
            subject: 'Пользователь {{ user.username }} приостановил рассылку',
            body: 'Массовая рассылка "{{ task.title }}" с ID {{ task.id }} приостановлена!'
          }
        }
      }
    },
    settingsValidation: () => null
  },
  'sendings.bulk.resumed': {
    settings: {
      me: {
        web: {
          enabledByDefault: false,
          template: {
            subject: 'Рассылка "{{ task.title }}" возобновлена',
            body: 'Задача на рассылку "{{ task.title }}" возобновлена.<br>Проверьте статус задачи в личном кабинете.'
          }
        }
      },
      manager: {
        web: {
          enabledByDefault: false,
          template: {
            subject: 'Пользователь {{ task.OwnerId }} возобновил рассылку',
            body: 'Массовая рассылка "{{ task.title }}" с ID {{ task.id }} возобновлена!'
          }
        }
      }
    },
    settingsValidation: () => null
  },
  'system.platform.issues': {
    settings: {
      me: {
        email: {
          enabledByDefault: false,
          template: {
            subject: 'Временные проблемы на сайте {{ domain }}',
            body: '{{ text }}'
          }
        },
        sms: {
          enabledByDefault: false,
          template: {
            body: 'На сайте {{ domain }} возникли временные проблемы.'
          }
        },
        web: {
          enabledByDefault: false,
          template: {
            subject: 'Временные проблемы на сайте',
            body: '{{ text }}'
          }
        }
        // get [NOTIFICATIONS_AVAILABLE_TYPES.push]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // },
        // get [NOTIFICATIONS_AVAILABLE_TYPES.tgBot]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // }
      }
    },
    settingsValidation: () => null
  },
  'system.route.issues': {
    settings: {
      me: {
        email: {
          enabledByDefault: false,
          template: {
            subject: 'Проблемы с маршрутизацией на сайте {{ domain }}',
            body: '{{ text }}'
          }
        },
        sms: {
          enabledByDefault: false,
          template: {
            body: 'На сайте {{ domain }} возникли проблемы с маршрутизацией.'
          }
        },
        web: {
          enabledByDefault: false,
          template: {
            subject: 'Проблемы с маршрутизацией на сайте',
            body: '{{ text }}'
          }
        }
        // get [NOTIFICATIONS_AVAILABLE_TYPES.push]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // },
        // get [NOTIFICATIONS_AVAILABLE_TYPES.tgBot]() {
        //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
        // }
      }
    },
    settingsValidation: () => null
  }

// 'payments.failed': {
  //   settings: {
  //     me: {
  //       email: {
  //         enabledByDefault: false,
  //         template: {
  //           subject: 'Платеж не прошел в личном кабинете по адресу: {{ domain }}',
  //           body: 'Уважаемый Клиент,<br><br>Платеж на сайте {{ domain }} не прошел.<br>Проверьте данные карты и попробуйте снова.<br>Логин: {{ user.username }} на <a href="{{ url }}">{{ domain }}</a><br><br>Если у вас возникли вопросы, свяжитесь со своим менеджером или напишите по адресу {{ manager.email }}.<br><br>С уважением {{ title }}'
  //         }
  //       },
  //       sms: {
  //         enabledByDefault: false,
  //         template: {
  //           body: 'Платеж на {{ domain }} не прошел.'
  //         }
  //       }
  //       // get [NOTIFICATIONS_AVAILABLE_TYPES.tgBot]() {
  //       //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
  //       // }
  //     },
  //     manager: {
  //       email: {
  //         enabledByDefault: false,
  //         template: {
  //           subject: 'Платеж пользователя {{ user.username }} не прошел',
  //           body: 'Платеж пользователя {{ user.username }} на сайте {{ domain }} не прошел.'
  //         }
  //       },
  //       sms: {
  //         enabledByDefault: false,
  //         template: {
  //           body: 'Платеж пользователя {{ user.username }} не прошел.'
  //         }
  //       }
  //       // get [NOTIFICATIONS_AVAILABLE_TYPES.tgBot]() {
  //       //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
  //       // }
  //     }
  //   },
  //   settingsValidation: () => null
  // }
// 'subscription.enabled': {
  //   settings: {
  //     me: {
  //       email: {
  //         enabledByDefault: false,
  //         template: {
  //           subject: 'Title',
  //           body: 'No template text!'
  //         }
  //       },
  //       web: {
  //         enabledByDefault: false,
  //         template: {
  //           subject: 'Title',
  //           body: 'No template text!'
  //         }
  //       }
  //       // get [NOTIFICATIONS_AVAILABLE_TYPES.push]() {
  //       //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
  //       // },
  //       // get [NOTIFICATIONS_AVAILABLE_TYPES.tgBot]() {
  //       //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
  //       // }
  //     },
  //     manager: {
  //       email: {
  //         enabledByDefault: false,
  //         template: {
  //           subject: 'Title',
  //           body: 'No template text!'
  //         }
  //       },
  //       web: {
  //         enabledByDefault: false,
  //         template: {
  //           subject: 'Title',
  //           body: 'No template text!'
  //         }
  //       }
  //       // get [NOTIFICATIONS_AVAILABLE_TYPES.push]() {
  //       //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
  //       // },
  //       // get [NOTIFICATIONS_AVAILABLE_TYPES.tgBot]() {
  //       //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
  //       // }
  //     }
  //   },
  //   settingsValidation: () => null
  // },
// 'subscription.renew.failed': {
  //   settings: {
  //     me: {
  //       email: {
  //         enabledByDefault: false,
  //         template: {
  //           subject: 'Title',
  //           body: 'No template text!'
  //         }
  //       },
  //       web: {
  //         enabledByDefault: false,
  //         template: {
  //           subject: 'Title',
  //           body: 'No template text!'
  //         }
  //       }
  //       // get [NOTIFICATIONS_AVAILABLE_TYPES.push]() {
  //       //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
  //       // },
  //       // get [NOTIFICATIONS_AVAILABLE_TYPES.tgBot]() {
  //       //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
  //       // }
  //     },
  //     manager: {
  //       email: {
  //         enabledByDefault: false,
  //         template: {
  //           subject: 'Title',
  //           body: 'No template text!'
  //         }
  //       },
  //       web: {
  //         enabledByDefault: false,
  //         template: {
  //           subject: 'Title',
  //           body: 'No template text!'
  //         }
  //       }
  //       // get [NOTIFICATIONS_AVAILABLE_TYPES.push]() {
  //       //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
  //       // },
  //       // get [NOTIFICATIONS_AVAILABLE_TYPES.tgBot]() {
  //       //   return this[NOTIFICATIONS_AVAILABLE_TYPES.email]
  //       // }
  //     }
  //   },
  //   settingsValidation: () => null
  // }
}

const NOTIFICATIONS_SETTINGS_MY = {}
const NOTIFICATIONS_SETTINGS_CHILD = {}

const NOTIFICATIONS_TEMPLATES = {}
const NOTIFICATIONS_TEMPLATES_MANAGER = {}

const NOTIFICATIONS_CHANNELS_PER_TYPE = {}

const templateCleanersByType = {
  [NOTIFICATIONS_AVAILABLE_TYPES.email]: template => {
    if (isSimpleObject(template)) {
      return {
        subject: ('' + (template.subject || '')).trim(),
        body: ('' + (template.body || '')).trim()
      }
    }
  },
  [NOTIFICATIONS_AVAILABLE_TYPES.web]: template => {
    if (isSimpleObject(template)) {
      return {
        subject: ('' + (template.subject || '')).trim(),
        body: ('' + (template.body || '')).trim()
      }
    }
  },
  [NOTIFICATIONS_AVAILABLE_TYPES.sms]: template => {
    if (isSimpleObject(template)) {
      return {
        body: ('' + (template.body || '')).trim()
      }
    }
  },
  [NOTIFICATIONS_AVAILABLE_TYPES.push]: template => {
    if (isSimpleObject(template)) {
      return {
        body: ('' + (template.body || '')).trim()
      }
    }
  },
  [NOTIFICATIONS_AVAILABLE_TYPES.tgBot]: template => {
    if (isSimpleObject(template)) {
      return {
        body: ('' + (template.body || '')).trim()
      }
    }
  }
}

const createNotificationCleaner = (notificationConfig, notificationConfigChannels) => {
  return function $cleaner(value) {
    if (typeof value !== 'object' || !value) {
      return undefined
    }

    const { channels, settings } = value
    const channelsFiltered = Array.isArray(channels) ? channels.filter(channel => ~notificationConfigChannels.indexOf(channel)) : []
    const settingsFiltered = notificationConfig.settingsValidation(settings)

    return {
      channels: channelsFiltered,
      settings: settingsFiltered,
      enabled: !!channelsFiltered.length
    }
  }
}

// Добавление настроек уведомлений в SETTINGS_USER_SCHEMA
for (const notificationType in NOTIFICATIONS_SETTINGS_TEMPLATE) {
  NOTIFICATIONS_CHANNELS_PER_TYPE[notificationType] = []

  const notificationConfig = NOTIFICATIONS_SETTINGS_TEMPLATE[notificationType]

  if (!notificationConfig) {
    continue
  }

  for (const level in notificationConfig.settings) {
    const channels = Object.keys(notificationConfig.settings[level])

    NOTIFICATIONS_CHANNELS_PER_TYPE[notificationType].push(...channels)

    const enabledByDefaultChannels = Object.keys(notificationConfig.settings[level]).filter(channel => notificationConfig.settings[level][channel].enabledByDefault)

    switch (level) {
      case 'me': {
        NOTIFICATIONS_SETTINGS_MY[`notifications.events.${notificationType}`] = {
          $default: {
            settings: notificationConfig.settingsValidation(),
            channels: enabledByDefaultChannels,
            get enabled() {
              return !!this.channels.length
            }
          },
          $cleaner: createNotificationCleaner(notificationConfig, channels),
          $defaulter
        }

        break
      }
      case 'manager': {
        NOTIFICATIONS_SETTINGS_CHILD[`notifications.childrenUsersEvents.${notificationType}`] = {
          $default: {
            settings: notificationConfig.settingsValidation({}),
            channels: enabledByDefaultChannels,
            get enabled() {
              return !!this.channels.length
            }
          },
          $cleaner: createNotificationCleaner(notificationConfig, channels),
          $defaulter
        }

        break
      }
    }

    for (const channel of channels) {
      const template = notificationConfig.settings[level][channel].template

      const setting = {
        $default: templateCleanersByType[channel](template),
        $cleaner: templateCleanersByType[channel],
        $defaulter
      }

      const settingName = `notifications.${channel}.templates.${notificationType}` // .${templateType}

      switch (level) {
        case 'me': {
          NOTIFICATIONS_TEMPLATES[settingName] = setting
          break
        }
        case 'manager': {
          NOTIFICATIONS_TEMPLATES_MANAGER[settingName + '.manager'] = setting
          break
        }
      }
    }
  }
}

for (const notificationType in NOTIFICATIONS_CHANNELS_PER_TYPE) {
  NOTIFICATIONS_CHANNELS_PER_TYPE[notificationType] = [ ...new Set(NOTIFICATIONS_CHANNELS_PER_TYPE[notificationType]) ]
}

exports.NOTIFICATIONS_SETTINGS_MY = NOTIFICATIONS_SETTINGS_MY
exports.NOTIFICATIONS_SETTINGS_CHILD = NOTIFICATIONS_SETTINGS_CHILD
exports.NOTIFICATIONS_TEMPLATES = NOTIFICATIONS_TEMPLATES
exports.NOTIFICATIONS_TEMPLATES_MANAGER = NOTIFICATIONS_TEMPLATES_MANAGER
exports.NOTIFICATIONS_CHANNELS_PER_TYPE = NOTIFICATIONS_CHANNELS_PER_TYPE

// const { inspect } = require('util')
// console.log(inspect(NOTIFICATIONS_SETTINGS_MY, {
//   depth: 10,
//   getters: true
// }))
// console.log(inspect(NOTIFICATIONS_SETTINGS_CHILD, {
//   depth: 10,
//   getters: true
// }))
// console.log(inspect(NOTIFICATIONS_TEMPLATES, {
//   depth: 10,
//   getters: true
// }))
// console.log(inspect(NOTIFICATIONS_TEMPLATES_MANAGER, {
//   depth: 10,
//   getters: true
// }))
